/**
 * Approval Timeline — Vertical stepper showing request progress
 */

import React from 'react';
import { FaCheck, FaTimes, FaCircle, FaClock } from 'react-icons/fa';

const ApprovalTimeline = ({ chain = [], currentStage, status, approvalLogs = [], request = null }) => {
  const getLogForStep = (step) => {
    if (step.stage === 'submitted') {
      return approvalLogs.find(l => l.action === 'submitted');
    }

    const actionFilter = ['approved', 're_approved', 'handover_accepted', 'manual_add', 'overridden', 'rejected'];

    // 1. Direct match by log stage
    let log = approvalLogs.find(
      l => l.stage === step.stage && actionFilter.includes(l.action)
    );
    if (log) return log;

    // 2. Direct match by role
    log = approvalLogs.find(
      l => (l.approverRole === step.role || (step.stage === 'handover' && l.approverRole === 'handover_employee')) &&
        actionFilter.includes(l.action)
    );
    if (log) return log;

    // 3. Match handover by action
    if (step.stage === 'handover') {
      log = approvalLogs.find(l => l.action === 'handover_accepted');
      if (log) return log;
    }

    // 4. Sequential match by index among approval steps
    const approvalSteps = chain.filter(s => s.stage !== 'submitted');
    const actionLogs = approvalLogs.filter(l => actionFilter.includes(l.action));
    const stepIdx = approvalSteps.findIndex(s => s.stage === step.stage);
    if (stepIdx !== -1 && actionLogs[stepIdx]) {
      return actionLogs[stepIdx];
    }

    // 5. Fallback if the request is approved and there is an override/manual_add log
    if (status === 'approved') {
      const overrideLog = approvalLogs.find(l => ['overridden', 'manual_add'].includes(l.action));
      if (overrideLog) return overrideLog;
    }

    return null;
  };

  const getStepStatus = (step, index) => {
    const currentStageIndex = chain.findIndex(s => s.stage === currentStage);

    if (status === 'rejected') {
      const stepLog = getLogForStep(step);
      if (stepLog && stepLog.action === 'rejected') return 'rejected';

      const rejStepIndex = chain.findIndex(s => {
        const sLog = getLogForStep(s);
        return sLog?.action === 'rejected';
      });

      if (rejStepIndex !== -1) {
        if (index < rejStepIndex) return 'completed';
        if (index === rejStepIndex) return 'rejected';
        return 'pending';
      }

      // Old fallback matching
      const rejLog = approvalLogs.find(l => l.action === 'rejected');
      if (rejLog) {
        const idx = chain.findIndex(s => s.stage === rejLog.stage || s.role === rejLog.approverRole || s.stage === currentStage);
        if (idx !== -1) {
          if (index < idx) return 'completed';
          if (index === idx) return 'rejected';
          return 'pending';
        }
      }
      return index === 0 ? 'completed' : 'pending';
    }

    if (status === 'cancelled') {
      if (step.stage === 'submitted') return 'completed';
      if (currentStageIndex !== -1 && index < currentStageIndex) return 'completed';
      const stepLog = getLogForStep(step);
      if (stepLog && ['approved', 're_approved', 'handover_accepted', 'manual_add', 'overridden'].includes(stepLog.action)) {
        return 'completed';
      }
      return 'pending';
    }

    if (status === 'approved') return 'completed';

    if (step.stage === 'submitted') return 'completed'; // always completed

    // Any stage prior to current waiting stage has already been completed/approved!
    if (currentStageIndex !== -1 && index < currentStageIndex) {
      return 'completed';
    }

    // Find if this stage has been approved in logs
    const stepLog = getLogForStep(step);
    if (stepLog && ['approved', 're_approved', 'handover_accepted', 'manual_add', 'overridden'].includes(stepLog.action)) {
      return 'completed';
    }

    // Is this the current waiting stage?
    if (step.stage === currentStage) return 'current';

    // Future stage
    return 'pending';
  };

  const formatDate = (ts) => {
    if (!ts) return null;
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="wrms-timeline">
      {chain.map((step, index) => {
        const stepStatus = getStepStatus(step, index);
        const log = getLogForStep(step);
        const isLast = index === chain.length - 1;

        return (
          <div key={step.stage} className="wrms-timeline-item">
            <div className="wrms-timeline-left">
              <div className={`wrms-timeline-dot ${stepStatus}`}>
                {stepStatus === 'completed' && <FaCheck size={12} />}
                {stepStatus === 'current' && <FaClock size={12} />}
                {stepStatus === 'rejected' && <FaTimes size={12} />}
                {stepStatus === 'pending' && <FaCircle size={8} />}
              </div>
              {!isLast && (
                <div className={`wrms-timeline-line line-${stepStatus === 'completed' ? 'completed' : stepStatus === 'current' ? 'current' : 'pending'}`} />
              )}
            </div>

            <div className="wrms-timeline-content">
              <div className={`wrms-timeline-label ${stepStatus === 'pending' ? 'muted' : ''}`}>
                {step.label}
              </div>

              {log ? (
                <>
                  <div className="wrms-timeline-sub">
                    {log.approverName || (step.stage === 'handover' && request?.handoverTo ? request.handoverTo : step.label)} &mdash; {formatDate(log.actedAt) || 'Approved'}
                  </div>
                  {log.remarks && log.action !== 'submitted' && (
                    <div className={`wrms-timeline-remarks`}
                      style={log.action === 'rejected'
                        ? { color: '#dc2626', background: '#fef2f2' }
                        : log.action === 'cancelled'
                        ? { color: '#475569', background: '#f1f5f9' }
                        : { color: '#059669', background: '#ecfdf5' }
                      }
                    >
                      {log.remarks}
                    </div>
                  )}
                </>
              ) : stepStatus === 'current' && status !== 'cancelled' ? (
                <div className="wrms-timeline-sub" style={{ color: '#d97706', fontWeight: 500 }}>
                  {status === 'on_hold' ? 'On Hold' : 'Awaiting approval...'}
                </div>
              ) : stepStatus === 'completed' ? (
                <div className="wrms-timeline-sub" style={{ color: '#059669', fontWeight: 500 }}>
                  {step.stage === 'submitted'
                    ? (request?.employeeName ? `${request.employeeName} — Submitted` : 'Submitted')
                    : step.stage === 'handover' && request?.handoverTo
                    ? `${request.handoverTo} — Approved`
                    : 'Approved'}
                </div>
              ) : (
                <div className="wrms-timeline-sub">{status === 'cancelled' ? '—' : 'Pending'}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ApprovalTimeline;
